import { Tooltip } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import ReactHotkeys from 'react-hot-keys';
import styled from 'styled-components';

import ClickSvg from '../../assets/icons/ClickSvg';
import useAccessBadge, { LicenseCoarseLevels } from '../../hooks/useAccessBadge';
import { useAttachmentContent } from '../../hooks/useAttachmentContent';
import { DesignerActions, useDesignerContext } from '../../hooks/useDesignerState';
import { useMetrics } from '../../hooks/useMetrics';
import usePaywallPrompt from '../../hooks/usePaywallPrompt';
import { useTextToSpeech } from '../../hooks/useTextToSpeech';
import { useRegisterLivePassage } from '../../providers/LivePassageProvider';
import { LessonAttachment } from '../../services/backendService/backendService';
import { IconWrapper } from '../_atoms/IconWrapper';
import {
  clampReadAloudFontSize,
  READ_ALOUD_FONT_SIZE_STEP,
  readStoredReadAloudFontSize,
  writeStoredReadAloudFontSize,
} from '../ReadAloud/readAloudFontSizeStorage';
import ReadAloudHighlightedTextViewer from '../ReadAloud/ReadAloudHighlightedTextViewer';
import VoiceModeControls, { VOICE_PRESETS } from '../ReadAloud/VoiceModeControls';
import {
  READ_ALOUD_FADE_HEIGHT,
  READ_ALOUD_SCROLLBAR_WIDTH,
  ReadAloudModal,
} from '../ReadAloudModal';
import { isTTSEnabledByCountryOrSubject } from './isTTSEnabledByCountryOrSubject';
import ReadAloudTextViewer from './ReadAloudTextViewer';
import { parseSlateStringToText, SlateEditable, SlateWrapper } from './slateUtil';
import { SlideElementAttachmentPassageType } from './slide.types';

/**
 * Text size for the passage, remembered across sessions.
 *
 * A hook rather than two copies of the same three handlers: ReadAloud and
 * ReadAloudWithContent both own this control, and persistence gives it a side
 * effect that has to fire on exactly one path in both.
 */
const useReadAloudFontSize = () => {
  const [readingState, setReadingState] = useState<{ size: number }>(() => ({
    size: readStoredReadAloudFontSize(),
  }));
  // Each step is measured from the ref, not from `readingState`: a teacher
  // jabbing A+ fires several clicks inside one React batch, and every handler
  // in that batch closes over the same not-yet-re-rendered size, so twenty
  // presses would advance the text by one step. The alternative — a functional
  // updater — can't own the write, because React is free to run it twice.
  const sizeRef = useRef(readingState.size);

  const step = (delta: number) => {
    const size = clampReadAloudFontSize(sizeRef.current + delta);
    sizeRef.current = size;
    setReadingState({ size });
    writeStoredReadAloudFontSize(size);
  };

  return {
    readingState,
    handleIncreaseFontSize: () => step(READ_ALOUD_FONT_SIZE_STEP),
    handleReduceFontSize: () => step(-READ_ALOUD_FONT_SIZE_STEP),
  };
};

export const ReadAloud = ({
  lessonAttachment,
  isFromDiscover,
  subject,
}: {
  lessonAttachment: LessonAttachment;
  isFromDiscover: boolean;
  // Lesson subject — passed in by the parent so this works during a
  // live game where `useDesignerContext` isn't populated. When
  // undefined, the helper falls back to the country gate only.
  subject?: string;
}) => {
  const { content } = useAttachmentContent(lessonAttachment, isFromDiscover);

  const {
    readingState,
    handleIncreaseFontSize,
    handleReduceFontSize,
  } = useReadAloudFontSize();

  const contentString = useMemo(() => {
    if (!content) return '';
    return JSON.stringify(content);
  }, [content]);

  const { accessBadge } = useAccessBadge();
  const country = accessBadge?.organization?.country;
  // Pass the raw tri-state through; the helper checks `=== true` so
  // `null` / `undefined` (never asked) and `false` (explicit "local
  // school") both skip the ela+intl TTS path.
  const isInternational = accessBadge?.organization?.isInternational;
  const isTTSEnabled = isTTSEnabledByCountryOrSubject({
    country,
    subject,
    isInternational,
  });
  const tts = useTextToSpeech(isTTSEnabled ? contentString : null);

  const { promptPaywall, paywallComponent, userAccessBadge } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });
  const coarseLevel = userAccessBadge?.license.coarseLevel;
  // US tenants need School plan (Enterprise); other supported countries (GB/AU/CA/IE/NZ) need Premium or higher.
  const isAllowed =
    country === 'US'
      ? coarseLevel === LicenseCoarseLevels.enterprise
      : coarseLevel === LicenseCoarseLevels.premium ||
        coarseLevel === LicenseCoarseLevels.enterprise;
  const metrics = useMetrics();

  const handleGenerate = (voiceId: string) => {
    if (!isAllowed) {
      metrics.logEvent('TTS.PaywallTriggered', {
        attachmentId: lessonAttachment.attachmentId,
        voiceId,
        country,
        coarseLevel,
      });
      promptPaywall({ label: 'Read Aloud TTS', description: 'Text to speech' });
      return;
    }
    metrics.logEvent('TTS.Generate', {
      attachmentId: lessonAttachment.attachmentId,
      voiceId,
      voiceLabel: VOICE_PRESETS.find(v => v.voiceId === voiceId)?.label,
    });
    tts.generate(voiceId);
  };

  // Stop TTS on unmount (e.g. modal close)
  useEffect(() => {
    return () => {
      tts.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isVoiceActive =
    tts.state === 'playing' || tts.state === 'paused' || tts.state === 'loading';

  return (
    <ReadAloudContainer>
      <ControlPanelContainer>
        <ControlPanel>
          <Tooltip title="Increase text size" placement="left">
            <ControlPanelButtonEnlarge onClick={handleIncreaseFontSize}>
              <span>A</span>
            </ControlPanelButtonEnlarge>
          </Tooltip>
          <Tooltip title="Decrease text size" placement="left">
            <ControlPanelButtonMinimize onClick={handleReduceFontSize}>
              <span>A</span>
            </ControlPanelButtonMinimize>
          </Tooltip>
          {isTTSEnabled && (
            <>
              <Divider />
              <VoiceModeControls
                state={tts.state}
                error={tts.error}
                analyserNode={tts.analyserNode}
                volume={tts.volume}
                playbackRate={tts.playbackRate}
                attachmentId={lessonAttachment.attachmentId}
                onGenerate={handleGenerate}
                onPlay={tts.play}
                onPause={tts.pause}
                onStop={tts.stop}
                onSeek={tts.seek}
                onVolumeChange={tts.setVolume}
                onPlaybackRateChange={tts.setPlaybackRate}
              />
            </>
          )}
        </ControlPanel>
      </ControlPanelContainer>
      {isTTSEnabled && isVoiceActive && tts.wordTimings.length > 0 ? (
        <ReadAloudHighlightedTextViewer
          content={contentString}
          id={lessonAttachment.attachmentId}
          forceState={{
            size: readingState.size,
            align: 'left',
            color: 'black',
          }}
          wordTimings={tts.wordTimings}
          currentWordIndex={tts.currentWordIndex}
        />
      ) : (
        <ReadAloudTextViewer
          content={contentString}
          id={lessonAttachment.attachmentId}
          forceState={{
            size: readingState.size,
            align: 'left',
            color: 'black',
          }}
        />
      )}
      {paywallComponent}
    </ReadAloudContainer>
  );
};

/** Same as ReadAloud but takes content directly (Slate JSON string) instead of loading via useAttachmentContent. */
export const ReadAloudWithContent = ({
  content,
  id = 'read-aloud-with-content',
  subject,
}: {
  content: string;
  id?: string;
  // Lesson subject — passed in by the parent so this works during a
  // live game where `useDesignerContext` isn't populated. When
  // undefined, the helper falls back to the country gate only.
  subject?: string;
}) => {
  const {
    readingState,
    handleIncreaseFontSize,
    handleReduceFontSize,
  } = useReadAloudFontSize();

  const { accessBadge } = useAccessBadge();
  const country = accessBadge?.organization?.country;
  // Pass the raw tri-state through; the helper checks `=== true` so
  // `null` / `undefined` (never asked) and `false` (explicit "local
  // school") both skip the ela+intl TTS path.
  const isInternational = accessBadge?.organization?.isInternational;
  const isTTSEnabled = isTTSEnabledByCountryOrSubject({
    country,
    subject,
    isInternational,
  });
  const tts = useTextToSpeech(isTTSEnabled ? content : null);

  const { promptPaywall, paywallComponent, userAccessBadge } = usePaywallPrompt({
    startState: { screen: 'schoolPlanPaywall' },
  });
  const coarseLevel = userAccessBadge?.license.coarseLevel;
  const isAllowed =
    country === 'US'
      ? coarseLevel === LicenseCoarseLevels.enterprise
      : coarseLevel === LicenseCoarseLevels.premium ||
        coarseLevel === LicenseCoarseLevels.enterprise;
  const metrics = useMetrics();

  const handleGenerate = (voiceId: string) => {
    if (!isAllowed) {
      metrics.logEvent('TTS.PaywallTriggered', {
        attachmentId: id,
        voiceId,
        country,
        coarseLevel,
      });
      promptPaywall({ label: 'Read Aloud TTS', description: 'Text to speech' });
      return;
    }
    metrics.logEvent('TTS.Generate', {
      attachmentId: id,
      voiceId,
      voiceLabel: VOICE_PRESETS.find(v => v.voiceId === voiceId)?.label,
    });
    tts.generate(voiceId);
  };

  useEffect(() => {
    return () => {
      tts.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isVoiceActive =
    tts.state === 'playing' || tts.state === 'paused' || tts.state === 'loading';

  return (
    <ReadAloudContainer>
      <ControlPanelContainer>
        <ControlPanel>
          <Tooltip title="Increase font size" placement="left">
            <ControlPanelButtonEnlarge onClick={handleIncreaseFontSize}>
              <span>A</span>
            </ControlPanelButtonEnlarge>
          </Tooltip>
          <Tooltip title="Decrease font size" placement="left">
            <ControlPanelButtonMinimize onClick={handleReduceFontSize}>
              <span>A</span>
            </ControlPanelButtonMinimize>
          </Tooltip>
          {isTTSEnabled && (
            <>
              <Divider />
              <VoiceModeControls
                state={tts.state}
                error={tts.error}
                analyserNode={tts.analyserNode}
                volume={tts.volume}
                playbackRate={tts.playbackRate}
                attachmentId={id}
                onGenerate={handleGenerate}
                onPlay={tts.play}
                onPause={tts.pause}
                onStop={tts.stop}
                onSeek={tts.seek}
                onVolumeChange={tts.setVolume}
                onPlaybackRateChange={tts.setPlaybackRate}
              />
            </>
          )}
        </ControlPanel>
      </ControlPanelContainer>
      {isTTSEnabled && isVoiceActive && tts.wordTimings.length > 0 ? (
        <ReadAloudHighlightedTextViewer
          content={content}
          id={id}
          forceState={{
            size: readingState.size,
            align: 'left',
            color: 'black',
          }}
          wordTimings={tts.wordTimings}
          currentWordIndex={tts.currentWordIndex}
        />
      ) : (
        <ReadAloudTextViewer
          content={content}
          id={id}
          forceState={{
            size: readingState.size,
            align: 'left',
            color: 'black',
          }}
        />
      )}
      {paywallComponent}
    </ReadAloudContainer>
  );
};

const ControlPanelButton = styled.div`
  color: white;
  width: 100%;
  text-align: center;
  height: 50px;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    background: black;
  }
  border-radius: 5px;
  padding: 10px;
  span {
    user-select: none;
  }
`;

const ControlPanelButtonMinimize = styled(ControlPanelButton)`
  font-size: 24px;
  line-height: 20px;
`;
const ControlPanelButtonEnlarge = styled(ControlPanelButton)`
  font-size: 38px;
  line-height: 32px;
`;

const ControlPanelContainer = styled.div`
  position: absolute;
  top: 50%;
  left: -65px;
  transform: translateY(-50%);
  border-radius: 10px;
  padding: 5px;
  background: rgba(17, 23, 29, 0.6);
  color: white;
  box-shadow: 0px 0px 0px 1px rgba(64, 87, 109, 0.04),
    0px 6px 20px -4px rgba(64, 87, 109, 0.3);

  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
`;

const ControlPanel = styled.div`
  position: relative;
`;

const Divider = styled.div`
  height: 1px;
  width: 80%;
  margin: 4px auto;
  background: rgba(255, 255, 255, 0.2);
`;

const ReadAloudContainer = styled.div`
  background: white;
  box-sizing: border-box;
  height: 100%;
  width: 100%;
  overflow-y: auto;
  border-radius: 10px;

  /* The padding lives on the scroller rather than on the modal card: a
     scrollbar is laid out against the padding-box edge, so putting the inset
     here keeps the text off the edges while the bar itself stays flush with
     the card's border instead of floating 20px inside it.

     Top and bottom match the card's fade height, so at rest the fade sits over
     blank space and the first and last lines of the passage stay crisp — text
     only dissolves once it scrolls under the fade. */
  padding: ${READ_ALOUD_FADE_HEIGHT}px 20px;

  /* Where scrollIntoView is allowed to park the TTS highlight, which is how the
     read-along keeps the spoken word visible. The top inset matches the fade so
     a backward seek can't land a word underneath it. The big bottom inset is
     what keeps the word readable: with the default (no inset) each word past
     the fold was dragged to the very bottom edge — measured at 97% of the
     card's height, i.e. inside the bottom fade — whereas this parks it around
     52%, with the rest of the sentence visible below. Words still in the upper
     half aren't scrolled at all, so playback doesn't lurch at the start, and
     the last lines drift below centre simply because the scroll has run out. */
  scroll-padding: ${READ_ALOUD_FADE_HEIGHT}px 0 45%;

  /* Chrome/Safari. Sizing the scrollbar here also opts out of macOS overlay
     scrollbars, so the bar stays visible — on a projected passage it's the only
     hint that there's more text below. Note the standard scrollbar-width /
     scrollbar-color properties are deliberately NOT set alongside these: Chrome
     ignores every ::-webkit-scrollbar rule as soon as either is present, which
     silently hands back the overlay bar. */
  &::-webkit-scrollbar {
    width: ${READ_ALOUD_SCROLLBAR_WIDTH}px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
    /* Keeps the thumb clear of the card's rounded corners. */
    margin: 10px 0;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(40, 47, 51, 0.25);
    background-clip: content-box;
    border: 2px solid transparent;
    border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(40, 47, 51, 0.4);
    background-clip: content-box;
  }

  /* Firefox only (nothing else implements -moz-appearance), which ignores the
     rules above. Gated rather than set unconditionally for the reason noted
     there. */
  @supports (-moz-appearance: none) {
    scrollbar-width: thin;
    scrollbar-color: rgba(40, 47, 51, 0.25) transparent;
  }

  /* Slate sizes both of these to 100%, so a passage longer than the card
     overflows them rather than growing them. A scroll container only adds its
     bottom padding after the last in-flow box — never after content spilling
     out of one — so without this the padding above is silently dropped at the
     end of the scroll and the closing line sits under the fade, permanently
     half-erased. Scoped to the read-aloud modal; slides rely on the 100%. */
  ${SlateWrapper}, ${SlateEditable} {
    height: auto;
  }
`;

const SlideElementAttachmentPassage = ({
  element,
  lessonAttachments,
  slideMode,
  isFromDiscover,
  subject,
}: {
  element: SlideElementAttachmentPassageType;
  style?: React.CSSProperties;
  lessonAttachments?: LessonAttachment[];
  slideMode: 'view' | 'edit';
  isFromDiscover: boolean;
  // Lesson subject for the TTS-eligibility check. Must be supplied by
  // the parent because this component is rendered in two contexts:
  //  - the designer (parent reads from useDesignerContext.template.metadata)
  //  - live game / curi.live (parent reads from game.metadata.subject)
  // Neither source is universally available here, so we delegate.
  subject?: string;
}) => {
  const lessonAttachment = useMemo(
    () => lessonAttachments?.find(l => l.attachmentId === element.attachmentId),
    [lessonAttachments, element.attachmentId],
  );

  const title = useMemo(() => {
    if (!lessonAttachment) return 'No title';
    return parseSlateStringToText(lessonAttachment.title);
  }, [lessonAttachment]);

  // In view mode the open/closed state is sourced from the
  // LivePassageProvider when it's mounted (LiveGame), so the clicker
  // can drive open-next / close-current across every attachment on the
  // slide. Outside a LivePassageProvider (discover previews, student
  // views, designer thumbnails) we fall back to local state so a tap
  // still opens the modal. In edit mode the designer's own preview
  // state drives it (unchanged).
  const [localShowReadAloud, setLocalShowReadAloud] = useState(false);
  const livePassage = useRegisterLivePassage(element.id, slideMode === 'view');
  const showReadAloudInView = livePassage ? livePassage.isOpen : localShowReadAloud;
  const setShowReadAloudInView = (open: boolean) => {
    if (livePassage) livePassage.setOpen(open);
    else setLocalShowReadAloud(open);
  };

  const dispatch = useDesignerContext(c => c.dispatch);
  const previewElementId = useDesignerContext(
    c => c.state.ui.previewAttachmentPassageElementId,
  );
  const isPreviewOpenInEdit = slideMode === 'edit' && previewElementId === element.id;

  const showReadAloud = slideMode === 'view' ? showReadAloudInView : isPreviewOpenInEdit;

  const closeReadAloud = () => {
    if (slideMode === 'view') {
      setShowReadAloudInView(false);
    } else {
      dispatch({ type: DesignerActions.closeAttachmentPassagePreview });
    }
  };

  return (
    <div
      style={{
        background: '#3e7e69',
        height: '100%',
        width: '100%',
        borderRadius: 10,
        cursor: slideMode === 'edit' ? 'default' : 'pointer',
        boxShadow: '4px 8px 1px #61b195',
        position: 'relative',
        overflow: 'hidden',
        padding: '20px',
      }}
      onClick={() => {
        if (slideMode === 'view') {
          setShowReadAloudInView(true);
        }
      }}
    >
      <ReactHotkeys
        keyName={'Escape'}
        onKeyDown={(_, event) => {
          event.preventDefault();
          if (showReadAloud) {
            closeReadAloud();
          }
        }}
      />
      <TopCircle />
      <ClickIconContainer>
        <IconWrapper color="#61b195" height="40%" width="40%">
          <ClickSvg />
        </IconWrapper>
      </ClickIconContainer>

      {showReadAloud && lessonAttachment && (
        <ReadAloudModal onClose={closeReadAloud}>
          <ReadAloud
            lessonAttachment={lessonAttachment}
            isFromDiscover={isFromDiscover}
            subject={subject}
          />
        </ReadAloudModal>
      )}
      <span
        style={{
          position: 'absolute',
          top: '45%',
          left: 20,
          width: 'calc(100% - 40px)',
          textAlign: 'center',
          color: 'white',
          fontSize: `${element.width / 12}px`,
          userSelect: 'none',
        }}
      >
        {title}
      </span>
    </div>
  );
};

const ClickIconContainer = styled.div`
  position: absolute;
  top: 15%;
  left: 45%;
  height: 40%;
  width: 40%;
  display: grid;
  justify-content: left;
  align-items: start;
`;

const TopCircle = styled.div`
  height: 50%;
  width: 50%;

  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -40%);

  border-radius: 9999px;
  background: #93c8b6;
  box-shadow: 0px 8px 1px #61b195;
`;

export default SlideElementAttachmentPassage;
